import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ToggleGroup from 'components/controls/ToggleGroup';
import TimeRangePicker from 'components/controls/TimeRangePicker';

interface TimeRangeSelectorProps {
    fromDate: Date;
    toDate: Date;
    onChange: (fromDate: Date, toDate: Date) => void;
    refreshNonce?: number;
}

type PresetValue = '15m' | '1h' | '3h' | '12h' | '24h' | '3d' | '7d' | 'custom';

// Hours to look back for each preset
const PRESET_HOURS: Record<Exclude<PresetValue, 'custom'>, number> = {
    '15m': 0.25,
    '1h': 1,
    '3h': 3,
    '12h': 12,
    '24h': 24,
    '3d': 72,
    '7d': 168,
};

const DEFAULT_PRESET: PresetValue = '3h';

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({ fromDate, toDate, onChange, refreshNonce }) => {
    const { t } = useTranslation();
    const [activePreset, setActivePreset] = useState<PresetValue>(DEFAULT_PRESET);
    const [customOpen, setCustomOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Close the custom dropdown on an outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setCustomOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Slide the active preset window forward to now on a refresh,
    // a custom range is an explicit choice and stays untouched
    useEffect(() => {
        if (!refreshNonce || activePreset === 'custom') {
            return;
        }

        const to = new Date();
        const from = new Date(to.getTime() - PRESET_HOURS[activePreset] * 60 * 60 * 1000);
        onChange(from, to);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshNonce]);

    // Handles a selection in the toggle group
    const handleToggleChange = (value: PresetValue) => {
        setActivePreset(value);

        if (value === 'custom') {
            setCustomOpen(true);
            return;
        }

        setCustomOpen(false);
        const to = new Date();
        const from = new Date(to.getTime() - PRESET_HOURS[value] * 60 * 60 * 1000);
        onChange(from, to);
    };

    const options: { value: PresetValue; label: string }[] = [
        { value: '15m', label: t('dashboard.monitoring.time_range.preset_15m') },
        { value: '1h', label: t('dashboard.monitoring.time_range.preset_1h') },
        { value: '3h', label: t('dashboard.monitoring.time_range.preset_3h') },
        { value: '12h', label: t('dashboard.monitoring.time_range.preset_12h') },
        { value: '24h', label: t('dashboard.monitoring.time_range.preset_24h') },
        { value: '3d', label: t('dashboard.monitoring.time_range.preset_3d') },
        { value: '7d', label: t('dashboard.monitoring.time_range.preset_7d') },
        { value: 'custom', label: t('dashboard.monitoring.time_range.custom_label') },
    ];

    return (
        <div className='time-range-selector' ref={wrapperRef}>
            <ToggleGroup value={activePreset} onChange={handleToggleChange} options={options} />
            {/* Show the custom range picker only once opened */}
            {customOpen && (
                <div className='time-range-dropdown'>
                    <TimeRangePicker fromDate={fromDate} toDate={toDate} onChange={onChange} />
                </div>
            )}
        </div>
    );
};

export default TimeRangeSelector;
